import { useState } from "react";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CountryPicker } from "@/components/CountryPicker";
import { CurrencyPicker } from "@/components/CurrencyPicker";
import { IndustryPicker, type Industry } from "@/components/IndustryPicker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";

interface CreateOrganisationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

export function CreateOrganisationDialog({
  open,
  onOpenChange,
  onCreated,
}: CreateOrganisationDialogProps) {
  const [name, setName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [country, setCountry] = useState("GB");
  const [defaultCurrency, setDefaultCurrency] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState<Industry | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function resetForm() {
    setName("");
    setContactEmail("");
    setCountry("GB");
    setDefaultCurrency("");
    setSelectedIndustry(null);
    setSubmitting(false);
    setError(null);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      resetForm();
    }
    onOpenChange(nextOpen);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError("Organisation name is required.");
      return;
    }
    if (!selectedIndustry?.id) {
      setError("Please select an industry.");
      return;
    }
    if (!contactEmail.trim()) {
      setError("Contact email is required.");
      return;
    }
    if (!country.trim()) {
      setError("Please select a country.");
      return;
    }

    setSubmitting(true);

    const { error: apiError } = await api.POST("/v1/organisations", {
      body: {
        name: name.trim(),
        industryId: selectedIndustry.id,
        contactEmail: contactEmail.trim(),
        country: country.trim().toUpperCase(),
        defaultCurrency: defaultCurrency.trim().toUpperCase() || null,
      },
    });

    if (apiError) {
      setError("Failed to create organisation.");
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    handleOpenChange(false);
    onCreated();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create Organisation</DialogTitle>
            <DialogDescription>
              Add a new organisation for this client.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="org-name">Name</Label>
              <Input
                id="org-name"
                placeholder="Organisation name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="org-industry">Industry</Label>
              <IndustryPicker
                id="org-industry"
                value={selectedIndustry}
                onChange={setSelectedIndustry}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="org-email">Contact Email</Label>
              <Input
                id="org-email"
                type="email"
                placeholder="contact@example.com"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="org-country">Country</Label>
                <CountryPicker
                  id="org-country"
                  value={country}
                  onChange={(code, c) => {
                    setCountry(code);
                    if (!defaultCurrency && c?.defaultCurrencies[0]) {
                      setDefaultCurrency(c.defaultCurrencies[0]);
                    }
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="org-currency">Default Currency</Label>
                <CurrencyPicker
                  id="org-currency"
                  value={defaultCurrency}
                  onChange={setDefaultCurrency}
                />
              </div>
            </div>

            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>

          <DialogFooter className="mt-6">
            <Button type="submit" disabled={submitting}>
              {submitting && <Spinner className="size-4" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
